import i18nFactory from '@yandex-int/i18n';
import { NormalizedCacheObject } from 'apollo-boost';
import * as Cookie from 'js-cookie';
import * as queryString from 'querystring';

import { getCSRFToken } from '../../service/ApiHelpers/ApiHelpers';
import { Fetch } from '../../service/Captcha/Captcha';
import { ApiMethodExecutingError } from '../../service/Errors';
import { DNAApolloClient } from '../DNAApolloClient/DNAApolloClient';

import * as keyset from './PerlRestApiMethodExecutor.i18n';

const restI18N = i18nFactory(keyset);

type HttpMethod = 'POST' | 'GET';

interface ApiMethodOptions {
    method: HttpMethod;
    contentType?: string;
    credentials?: RequestCredentials;
    mode?: RequestMode;
}

export enum PerlResponseType {
    json = 'json',
    text = 'text'
}

interface ExecuteApiMethodParams<Params> {
    methodPath: string;
    params: Params;
    options?: ApiMethodOptions;
    limit?: number;
    responseType?: PerlResponseType;
}

interface ExecuteAwaitApiMethodParams<Params> {
    methodPath: string;
    params: Params;
    options?: ApiMethodOptions;
    limit?: number;
    responseType?: PerlResponseType;
    login: string;
}

const defaultApiMethodOptions: ApiMethodOptions = {
    method: 'POST',
    credentials: 'same-origin',
    mode: 'cors'
};

export interface PerlRestApiBaseResult {
    ok: boolean;
    error?:
        | string
        | {
              message: string;
          };
    error_code?: string;
    csrf_token?: string;
}

export class PerlRestApiMethodExecutor {
    private baseUrl: string;
    private csrfToken: string;
    private fetch: Fetch;
    private apolloClient: DNAApolloClient<NormalizedCacheObject>;

    constructor(baseUrl: string, fetch: Fetch) {
        this.baseUrl = this.normalizeBaseUrl(baseUrl);
        this.csrfToken = getCSRFToken();
        this.fetch = fetch;
    }

    public setApolloClient(
        dnaApolloClient: DNAApolloClient<NormalizedCacheObject>
    ) {
        this.apolloClient = dnaApolloClient;
    }

    async executeAwaitApiMethod<Params, Result>({
        methodPath,
        params,
        options,
        login
    }: ExecuteAwaitApiMethodParams<Params>): Promise<Result> {
        const response = await this.makeRequest<
            Params,
            PerlRestApiBaseResult & Result
        >({
            methodPath,
            params,
            options
        });

        if (
            response.error_code === 'invalid_csrf' &&
            response.csrf_token &&
            response.csrf_token !== this.csrfToken
        ) {
            this.csrfToken = response.csrf_token;
            Cookie.set('_direct_csrf_token', response.csrf_token);

            return this.makeRequest<Params, PerlRestApiBaseResult & Result>({
                methodPath,
                params,
                options
            });
        }

        return this.apolloClient.awaitLastMutation(login).then(() => {
            return response;
        });
    }

    async executeApiMethod<Params, Result>({
        methodPath,
        params,
        options
    }: ExecuteApiMethodParams<Params>): Promise<Result> {
        const response = await this.makeRequest<
            Params,
            PerlRestApiBaseResult & Result
        >({
            methodPath,
            params,
            options
        });

        if (
            response.error_code === 'invalid_csrf' &&
            response.csrf_token &&
            response.csrf_token !== this.csrfToken
        ) {
            this.csrfToken = response.csrf_token;
            Cookie.set('_direct_csrf_token', response.csrf_token);

            return this.makeRequest<Params, PerlRestApiBaseResult & Result>({
                methodPath,
                params,
                options
            });
        }

        return response;
    }

    private async makeRequest<Params, Result>({
        methodPath,
        params,
        options,
        responseType = PerlResponseType.json
    }: ExecuteApiMethodParams<Params>): Promise<Result> {
        params = Object.assign({}, params, {
            csrf_token: this.csrfToken
        });

        const apiMethodOptions = Object.assign(
            {},
            defaultApiMethodOptions,
            options || {}
        );
        const { method } = apiMethodOptions;
        const url: string = this.getUrl(methodPath, params, method);
        const requestParams: RequestInit = this.getRequestParams(
            params,
            apiMethodOptions
        );
        const request: Request = new Request(url, requestParams);

        try {
            const response = await this.fetch(request);
            const data = await (responseType === PerlResponseType.json
                ? response.json()
                : response.text());

            if (data.error) {
                throw {
                    name: 'requestError',
                    message:
                        typeof data.error === 'string'
                            ? data.error
                            : data.error.message
                };
            }

            return data;
        } catch (error) {
            const message =
                error.name === 'requestError'
                    ? error.message
                    : restI18N('request-error');

            throw new ApiMethodExecutingError(message);
        }
    }

    private normalizeBaseUrl(baseUrl: string): string {
        return baseUrl[baseUrl.length - 1] === '/' ? baseUrl : `${baseUrl}/`;
    }

    private normalizeMethodPath(methodPath: string): string {
        return methodPath[0] === '/' ? methodPath.substr(1) : methodPath;
    }

    private getUrl(methodPath: string, params: {}, method: HttpMethod): string {
        let searchString: string = '';

        if (method === 'GET' && params) {
            searchString = '?' + queryString.stringify(params);
        }
        return `${this.baseUrl}${this.normalizeMethodPath(
            methodPath
        )}${searchString}`;
    }

    private getRequestParams(
        params: {},
        apiMethodOptions: ApiMethodOptions
    ): RequestInit {
        const requestParams: RequestInit = apiMethodOptions;

        if (apiMethodOptions.method === 'POST') {
            requestParams.body = queryString.stringify(params);
        }

        requestParams.headers = new Headers({
            'Content-Type':
                apiMethodOptions.method === 'POST'
                    ? 'application/x-www-form-urlencoded'
                    : 'application/json',
            Accept: 'application/json'
        });

        return requestParams;
    }
}
